import {
  Attributes,
  AttributesRow,
  Button,
  Card,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Box, Heading, HStack, Link, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil, mergeAll} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {
  GetServiceOrderIssueNoteResponse,
  PostServiceOrderItemRequest,
  PostServiceOrderItemResponse,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {customerRoutes, vehiclesRoutes, workshopRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  catchUnhandledDataGridActions,
  getCustomerName,
  queryParams,
} from '@dms/shared';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId, useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

export interface ServiceOrderTabProp extends RequiredTestIdProps {
  serviceOrderIssueNoteId: string | Nullish;
  serviceOrderId: string | Nullish;
  serviceCaseId: string | Nullish;
  serviceOrderAttributes?: GetServiceOrderIssueNoteResponse['serviceOrder'];
  onAddItemToBasket: (
    arg: PostServiceOrderItemRequest
  ) => Promise<PostServiceOrderItemResponse | void>;
  hasPendingRequest: boolean;
}
export function ServiceOrderTab(props: ServiceOrderTabProp) {
  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const vehicleQuery = useGetVehicleQuery(
    {vehicleId: props.serviceOrderAttributes?.vehicleId as string},
    {skip: isNilOrEmpty(props.serviceOrderAttributes?.vehicleId)}
  );

  const customerQuery = useGetCustomerV2Query(
    {customerId: props.serviceOrderAttributes?.customerId as string},
    {skip: isNilOrEmpty(props.serviceOrderAttributes?.customerId)}
  );

  const isLoading = vehicleQuery.isLoading || customerQuery.isLoading;
  const hasError = vehicleQuery.isError || customerQuery.isError;

  const customerName = getCustomerName(customerQuery.data);

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.number'),
      content:
        props.serviceCaseId && props.serviceOrderId ? (
          <Link
            title={props.serviceOrderAttributes?.number}
            rightIcon="action/open_in_new"
            size="small"
            href={composePath(workshopRoutes.serviceCaseDetail, {
              params: {id: props.serviceCaseId},
              queryParams: {
                [queryParams.SERVICE_CASE_ORDER_ID]: props.serviceOrderId,
              },
            })}
            target="_blank"
          />
        ) : (
          props.serviceOrderAttributes?.number
        ),
    },
    {
      label: i18n.t('entity.warehouse.labels.serviceOrderType'),
      value: props.serviceOrderAttributes?.type,
    },
    {
      label: i18n.t('entity.warehouse.labels.serviceOrderVariant'),
      value: props.serviceOrderAttributes?.variant,
    },
    {
      label: i18n.t('entity.warehouse.labels.customer'),
      content: props.serviceOrderAttributes?.customerId ? (
        <Link
          title={customerName}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(customerRoutes.detail, {
            params: {id: props.serviceOrderAttributes?.customerId},
          })}
          target="_blank"
        />
      ) : (
        customerName
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      content: props.serviceOrderAttributes?.vehicleId ? (
        <Link
          title={vehicleQuery.data?.title}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(vehiclesRoutes.detail, {
            params: {id: props.serviceOrderAttributes?.vehicleId},
          })}
          target="_blank"
        />
      ) : (
        vehicleQuery.data?.title
      ),
    },
    {
      label: i18n.t('entity.vehicle.labels.licensePlate'),
      value: vehicleQuery.data?.vehicle?.registrationPlate,
    },
    {
      label: i18n.t('entity.warehouse.labels.vin'),
      value: vehicleQuery.data?.vehicle?.vin,
    },
  ];

  const handleViewOrderInNewTab = () => {
    window.open(
      composePath(workshopRoutes.serviceCaseDetail, {
        params: {id: props.serviceCaseId},
      }),
      '_blank'
    );
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {serviceOrderId: props.serviceOrderId}]),
    [props.serviceOrderId]
  );

  const actionCallback: ActionCallback = async ({actionKey, rowId, refreshData}) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      throw Error('Row id must be specified');
    }

    await match(actionKey)
      .with('add', async () => {
        if (props.serviceOrderIssueNoteId) {
          await props
            .onAddItemToBasket({
              serviceOrderIssueNoteId: props.serviceOrderIssueNoteId,
              serviceOrderJobItemId: id,
            })
            .then(() => refreshData());
        }
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <VStack spacing={4} height="100%">
      <HStack justify="space-between">
        <Heading size={4}>{i18n.t('entity.warehouse.labels.order')}</Heading>
        <HStack>
          <Button
            title={i18n.t('entity.warehouse.labels.viewOrder')}
            variant="link"
            leftIcon="action/open_in_new"
            onClick={handleViewOrderInNewTab}
            data-testid={suffixTestId('actions.viewOrderInNewTab', props)}
          />
        </HStack>
      </HStack>

      <Card variant="inlineGrey">
        <DataStatus isLoading={isLoading} isError={hasError}>
          <Attributes rows={rows} size="quarter" data-testid={suffixTestId('attributes', props)} />
        </DataStatus>
      </Card>

      <Heading size={4}>{i18n.t('entity.warehouse.labels.addedSpareParts')}</Heading>

      <Box flex={1}>
        <DmsDataGrid
          // DG must be re-rendered on folder change to update the query modifier
          // eslint-disable-next-line no-restricted-syntax
          key={'issue-note-service-order-items-' + props.serviceOrderId}
          gridCode="issue-note-service-order-items"
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          data-testid={suffixTestId('serviceOrderList', props)}
        />
      </Box>
    </VStack>
  );
}
